import axios from 'axios';

const API_HOST = process.env.REACT_APP_API_HOST;

export type BookTimeParams = {
  kind: string;
  start: Date;
  end: Date;
};

export type BookRecurringTimeAwayParams = {
  /** Leave type key; defaults to working remotely. */
  kind?: string;
  workingDays: ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday')[];
  period: {
    value: number;
    type: 'weeks' | 'month';
    start: Date;
  };
};

async function bookTime(kind: string, start: any, end: any, note?: string) {
  const { data } = await axios.put(`${API_HOST}/v1/timeaway`, {
    kind,
    start: {
      date: start.date,
      time: start.time,
    },
    end: {
      date: end.date,
      time: end.time,
    },
    ...(note ? { description: note } : {}),
  });
  return data;
}

async function bookRecurringTimeAway(params: BookRecurringTimeAwayParams) {
  const { data } = await axios.put(`${API_HOST}/v1/timeaway/recurring`, {
    // Recurring requests default to remote working.
    kind: params.kind || 'working_remotely',
    workingDays: params.workingDays,
    period: {
      value: params.period.value,
      type: params.period.type,
      start: params.period.start,
    },
  });

  return data;
}

async function getTimeAway() {
  const { data } = await axios.get(`${API_HOST}/v1/timeaway`);
  return data;
}

async function deleteTimeawayRequest(id: string | number) {
  const { data } = await axios.delete(`${API_HOST}/v1/timeaway/${id}`);
  return data;
}

async function getTimeOffStatus() {
  const { data } = await axios.get(`${API_HOST}/v1/timeaway/stats`);
  return data;
}

async function getTimesOff(page?: number, limit?: number, mode?: string) {
  const { data } = await axios.get(`${API_HOST}/v1/timeaway/requests`, {
    params: { limit, page, mode },
  });
  return data;
}

async function getPersonalTimesOff(year: number = new Date().getFullYear()) {
  const { data } = await axios.get(
    `${API_HOST}/v1/timeaway/requests/personal`,
    {
      params: { year },
    },
  );
  return data;
}

/** One person's requests, by their stored user id (not the viewer's). */
async function getProfileTimesOff(
  userId: string,
  page?: number,
  count?: number,
) {
  const { data } = await axios.get(
    `${API_HOST}/v1/timeaway/requests/profile/${encodeURIComponent(userId)}`,
    { params: { page, count } },
  );
  // Unlike /requests, this endpoint returns rows without flattening `data`.
  return {
    ...data,
    data: (Array.isArray(data?.data) ? data.data : []).map(
      (item: any) => item?.data ?? item,
    ),
  };
}

async function getEmployeeDetails(employeeId: string) {
  const { data } = await axios.get(
    `${API_HOST}/v1/timeaway/${employeeId}/status`,
  );
  return data;
}

async function approveTimeawayRequest(timeawayId: string, note?: string) {
  const { data } = await axios.put(
    `${API_HOST}/v1/timeaway/${timeawayId}/approve`,
    {
      note,
    },
  );
  return data;
}

async function rejectTimeawayRequest(timeawayId: string, reason: string) {
  const { data } = await axios.put(
    `${API_HOST}/v1/timeaway/${timeawayId}/reject`,
    {
      reason,
    },
  );
  return data;
}

async function requestChangesTimeawayRequest(
  timeawayId: string,
  reason?: string,
) {
  const { data } = await axios.put(
    `${API_HOST}/v1/timeaway/${timeawayId}/request_changes`,
    {
      reason,
    },
  );
  return data;
}

async function bulkApproveTimeawayRequests(ids: string[], note?: string) {
  const { data } = await axios.put(`${API_HOST}/v1/timeaway/bulk/approve`, {
    ids,
    note,
  });
  return data;
}

async function bulkRejectTimeawayRequests(ids: string[], reason: string) {
  const { data } = await axios.put(`${API_HOST}/v1/timeaway/bulk/reject`, {
    ids,
    reason,
  });
  return data;
}

async function getCoverageAnalysis(id: string) {
  const { data } = await axios.get(`${API_HOST}/v1/timeaway/${id}/coverage`);
  return data;
}

const timeawayApi = {
  bookTime,
  bookRecurringTimeAway,
  getTimeAway,
  deleteTimeawayRequest,
  getTimeOffStatus,
  getTimesOff,
  getPersonalTimesOff,
  getProfileTimesOff,
  getEmployeeDetails,
  approveTimeawayRequest,
  rejectTimeawayRequest,
  requestChangesTimeawayRequest,
  bulkApproveTimeawayRequests,
  bulkRejectTimeawayRequests,
  getCoverageAnalysis,
};

export default timeawayApi;
